import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto, hoyCaracas } from '../utils/format';
import { pref } from '../utils/moneda';
import SelectorMoneda, { useVistaMoneda } from '../components/reportes/SelectorMoneda';
import Exportar, { descargarArchivo } from '../components/reportes/Exportar';
import EncabezadoImpresion from '../components/reportes/EncabezadoImpresion';
import BotonAyuda from '../components/BotonAyuda';
import { filaClickeable, FOCO_FILA } from '../utils/filaClickeable';

// ============================================================
// Flujo de caja histórico (pendiente #85): entradas y salidas reales por mes
// (con saldo al cierre de cada mes) y la misma información abierta por
// categoría. El backend manda cada monto en USD y en BCV, así el selector
// cambia de vista sin volver a pedir datos. Las transferencias entre cuentas
// propias no cuentan como entrada ni salida.
// ============================================================

const PESTANAS = [
  { key: 'mes', label: 'Por mes' },
  { key: 'categoria', label: 'Por categoría' }
];

// "2026-08" -> "ago 2026"
function mesCorto(mes) {
  const [a, m] = mes.split('-').map(Number);
  return new Date(a, m - 1, 1).toLocaleDateString('es', { month: 'short', year: 'numeric' }).replace('.', '');
}

function restarMeses(mes, n) {
  const [a, m] = mes.split('-').map(Number);
  const d = new Date(a, m - 1 - n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function Monto({ v, vista, clase = '' }) {
  return <span className={`whitespace-nowrap ${v < 0 ? 'text-red-600' : ''} ${clase}`}>{pref(vista)}{formatMonto(v)}</span>;
}

function TablaPorMes({ datos, k, vista }) {
  const filas = datos.por_mes;
  const totalIng = datos.total.ingresos[k];
  const totalEgr = datos.total.egresos[k];
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr>
              <th className="px-3 py-1.5">Mes</th>
              <th className="px-3 py-1.5 text-right">Entradas</th>
              <th className="px-3 py-1.5 text-right">Salidas</th>
              <th className="px-3 py-1.5 text-right">Neto</th>
              <th className="px-3 py-1.5 text-right">Saldo al cierre</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((r) => {
              const neto = r.ingresos[k] - r.egresos[k];
              return (
                <tr key={r.mes} className="border-t">
                  <td className="px-3 py-1.5 text-slate-700 capitalize">{mesCorto(r.mes)}</td>
                  <td className="px-3 py-1.5 text-right text-green-700"><Monto v={r.ingresos[k]} vista={vista} /></td>
                  <td className="px-3 py-1.5 text-right text-red-600"><Monto v={r.egresos[k]} vista={vista} /></td>
                  <td className="px-3 py-1.5 text-right font-semibold"><Monto v={neto} vista={vista} /></td>
                  <td className="px-3 py-1.5 text-right text-slate-700">
                    <Monto v={r.saldo_cierre[k]} vista={vista} />
                    {!r.saldo_cierre.completo && <span title="Faltan tasas para convertir alguna cuenta en Bs" className="text-amber-600"> *</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="border-t-2 font-semibold bg-slate-50">
            <tr>
              <td className="px-3 py-1.5">Total</td>
              <td className="px-3 py-1.5 text-right text-green-700"><Monto v={totalIng} vista={vista} /></td>
              <td className="px-3 py-1.5 text-right text-red-600"><Monto v={totalEgr} vista={vista} /></td>
              <td className="px-3 py-1.5 text-right"><Monto v={totalIng - totalEgr} vista={vista} /></td>
              <td className="px-3 py-1.5" />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function MatrizCategorias({ datos, k, vista }) {
  const [colapsados, setColapsados] = useState(new Set());
  const meses = datos.meses;

  function alternar(id) {
    setColapsados((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  }

  const celdas = (x, clase = '') => (
    <>
      {meses.map((mes) => (
        <td key={mes} className={`px-3 py-1.5 text-right ${clase}`}>
          {x.meses[mes][k] ? <Monto v={x.meses[mes][k]} vista={vista} /> : <span className="text-slate-300">—</span>}
        </td>
      ))}
      <td className={`px-3 py-1.5 text-right font-semibold ${clase}`}><Monto v={x.total[k]} vista={vista} /></td>
    </>
  );

  const seccion = (titulo, grupos, color) => (
    <>
      <tr className="border-t-2">
        <td colSpan={meses.length + 2} className={`px-3 py-1.5 font-bold ${color} sticky left-0 bg-white`}>{titulo}</td>
      </tr>
      {grupos.length === 0 && (
        <tr><td colSpan={meses.length + 2} className="px-3 py-1.5 text-slate-400">Sin movimientos</td></tr>
      )}
      {grupos.map((g) => {
        const abierto = !colapsados.has(g.id);
        return (
          <React.Fragment key={g.id}>
            <tr {...filaClickeable(() => alternar(g.id))} className={`border-t bg-slate-50 hover:bg-slate-100 cursor-pointer ${FOCO_FILA}`}>
              <td className="px-3 py-1.5 font-semibold text-slate-800 sticky left-0 bg-slate-50 whitespace-nowrap">
                <span className="inline-flex items-center gap-1">
                  {abierto ? <ChevronDown size={14} className="text-slate-400 print:hidden" /> : <ChevronRight size={14} className="text-slate-400 print:hidden" />}
                  {g.nombre}
                </span>
              </td>
              {celdas(g, 'font-semibold')}
            </tr>
            {abierto && g.categorias.length > 1 && g.categorias.map((c) => (
              <tr key={c.id} className="border-t">
                <td className="pl-8 pr-3 py-1.5 text-slate-600 sticky left-0 bg-white whitespace-nowrap">{c.nombre}</td>
                {celdas(c, 'text-slate-600')}
              </tr>
            ))}
          </React.Fragment>
        );
      })}
    </>
  );

  const neto = {
    meses: Object.fromEntries(datos.por_mes.map((r) => [r.mes, { [k]: r.ingresos[k] - r.egresos[k] }])),
    total: { [k]: datos.total.ingresos[k] - datos.total.egresos[k] }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr>
              <th className="px-3 py-1.5 sticky left-0 bg-slate-50">Grupo / categoría</th>
              {meses.map((mes) => <th key={mes} className="px-3 py-1.5 text-right capitalize whitespace-nowrap">{mesCorto(mes)}</th>)}
              <th className="px-3 py-1.5 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {seccion('Entradas', datos.categorias.ingresos, 'text-green-700')}
            {seccion('Salidas', datos.categorias.egresos, 'text-red-600')}
          </tbody>
          <tfoot className="border-t-2 bg-slate-50">
            <tr>
              <td className="px-3 py-1.5 font-bold sticky left-0 bg-slate-50">Neto</td>
              {celdas(neto, 'font-semibold')}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export default function ReporteFlujoCaja() {
  const { empresaActiva, empresaActivaNombre } = useAuth();
  const mesActual = hoyCaracas().slice(0, 7);
  // ?desde=AAAA-MM&hasta=AAAA-MM (enlace desde el Cierre del período, #121).
  const [params] = useSearchParams();
  const mesParam = (k) => (/^\d{4}-\d{2}$/.test(params.get(k) || '') ? params.get(k) : null);
  const [desde, setDesde] = useState(() => mesParam('desde') || restarMeses(mesActual, 11));
  const [hasta, setHasta] = useState(() => mesParam('hasta') || mesActual);
  const [cuentaId, setCuentaId] = useState('');
  const [cuentas, setCuentas] = useState([]);
  const [vista, setVista] = useVistaMoneda();
  const [pestana, setPestana] = useState('mes');
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!empresaActiva) return;
    setCuentaId('');
    api.get('/cuentas').then(({ data }) => setCuentas(data)).catch(() => setCuentas([]));
  }, [empresaActiva]);

  useEffect(() => {
    if (!empresaActiva || !desde || !hasta) return;
    if (desde > hasta) { setError('El mes "desde" no puede ser posterior a "hasta".'); setDatos(null); return; }
    setCargando(true);
    setError('');
    api.get('/reportes/flujo-caja', { params: { desde, hasta, cuenta_id: cuentaId || undefined } })
      .then(({ data }) => setDatos(data))
      .catch((err) => setError(err.response?.data?.error || 'No se pudo cargar el flujo de caja'))
      .finally(() => setCargando(false));
  }, [empresaActiva, desde, hasta, cuentaId]);

  const k = vista === 'BCV' ? 'bcv' : 'usd';
  const cuentaNombre = cuentas.find((c) => String(c.id) === String(cuentaId))?.nombre;

  async function exportarExcel() {
    const { data } = await api.get('/reportes/flujo-caja/excel', {
      params: { desde, hasta, cuenta_id: cuentaId || undefined, vista },
      responseType: 'blob'
    });
    descargarArchivo(data, `flujo-de-caja-${desde}-a-${hasta}${empresaActivaNombre ? `-${empresaActivaNombre}` : ''}.xlsx`);
  }


  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between print:hidden">
        <h1 className="text-2xl font-bold text-slate-800">
          Flujo de caja
          {empresaActivaNombre && <span className="text-slate-400 font-normal"> · {empresaActivaNombre}</span>}
          <BotonAyuda />
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="month" value={desde} max={hasta} onChange={(e) => e.target.value && setDesde(e.target.value)} className="border rounded-md px-3 py-2 text-sm" title="Desde" />
          <span className="text-slate-400 text-sm">a</span>
          <input type="month" value={hasta} min={desde} onChange={(e) => e.target.value && setHasta(e.target.value)} className="border rounded-md px-3 py-2 text-sm" title="Hasta" />
          <select value={cuentaId} onChange={(e) => setCuentaId(e.target.value)} className="border rounded-md px-3 py-2 text-sm">
            <option value="">Todas las cuentas</option>
            {cuentas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
          <SelectorMoneda vista={vista} onChange={setVista} />
          <Exportar onExcel={exportarExcel} disabled={!datos} />
        </div>
      </div>

      <EncabezadoImpresion titulo="Flujo de caja" detalle={`${mesCorto(desde)} a ${mesCorto(hasta)} · ${cuentaNombre || 'Todas las cuentas'} · montos en ${vista}`} />

      <div className="flex gap-1 border-b print:hidden">
        {PESTANAS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => setPestana(p.key)}
            className={`px-3.5 py-2.5 text-sm ${pestana === p.key ? 'font-semibold text-primary-600 border-b-2 border-primary-600' : 'text-slate-500 hover:text-slate-700'}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {cargando ? (
        <p className="text-slate-400 text-sm">Cargando…</p>
      ) : datos && (
        <>
          <div className="bg-white rounded-xl shadow-sm px-4 py-2.5 flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
            <span><span className="text-slate-500">Entradas </span><span className="text-lg font-bold text-green-700">{pref(vista)}{formatMonto(datos.total.ingresos[k])}</span></span>
            <span><span className="text-slate-500">Salidas </span><span className="text-lg font-bold text-red-600">{pref(vista)}{formatMonto(datos.total.egresos[k])}</span></span>
            <span><span className="text-slate-500">Neto </span><span className="text-lg font-bold text-slate-800"><Monto v={datos.total.ingresos[k] - datos.total.egresos[k]} vista={vista} /></span></span>
          </div>

          {datos.sin_tasa > 0 && (
            <p className="flex items-center gap-1.5 text-sm text-amber-700">
              <AlertTriangle size={15} />
              {datos.sin_tasa} movimiento(s) en Bs de días sin tasa cargada no se pudieron convertir y no suman en el reporte.
            </p>
          )}

          {pestana === 'mes'
            ? <TablaPorMes datos={datos} k={k} vista={vista} />
            : <MatrizCategorias datos={datos} k={k} vista={vista} />}
        </>
      )}
    </div>
  );
}
